"use client";

import { useEffect, useState } from 'react';

// Client-side routers (Next.js, React Router, etc.) navigate via
// history.pushState/replaceState, which fire no event of their own — only
// popstate (back/forward) does. Patch both once per page so every SPA
// navigation is observable, and broadcast it as a real event other code can
// listen for too.
let patched = false;

function patchHistoryOnce() {
  if (patched || typeof window === 'undefined') return;
  patched = true;

  const dispatchLocationChange = () =>
    window.dispatchEvent(new Event('markup:locationchange'));

  const originalPushState = history.pushState.bind(history);
  history.pushState = (...args: Parameters<History['pushState']>) => {
    originalPushState(...args);
    dispatchLocationChange();
  };

  const originalReplaceState = history.replaceState.bind(history);
  history.replaceState = (...args: Parameters<History['replaceState']>) => {
    originalReplaceState(...args);
    dispatchLocationChange();
  };

  window.addEventListener('popstate', dispatchLocationChange);
}

/**
 * Tracks the current page URL, updating on SPA client-side navigation
 * (pushState/replaceState) as well as back/forward (popstate) — not just
 * full page loads.
 */
export function useCurrentUrl(): string {
  const [url, setUrl] = useState(() =>
    typeof window !== 'undefined' ? window.location.href : ''
  );

  useEffect(() => {
    patchHistoryOnce();

    const handleChange = () => setUrl(window.location.href);
    window.addEventListener('markup:locationchange', handleChange);
    window.addEventListener('popstate', handleChange);

    // Catch navigations that happened between initial render and this
    // effect's mount.
    handleChange();

    return () => {
      window.removeEventListener('markup:locationchange', handleChange);
      window.removeEventListener('popstate', handleChange);
    };
  }, []);

  return url;
}
